// Infers a lead's interested service (the CRM "Giải pháp" field) from the
// service page the visitor came through, so a generic signup — which only sends
// an email — no longer lands in the CRM with a blank product.
//
// The self-serve consoles live at `/services/<slug>` (see ServicesPage routes).
// A visitor who opens `/services/kubernetes` and then registers submits via the
// generic register form (email only); without this inference the lead has no
// product group. We remember the slug when an unauthenticated visitor is bounced
// to auth, and also read the first-touch landing URL, then map slug → the
// canonical DataCentral product group.

import { PRODUCT_GROUP } from './lead-product-group';
import { getAttribution } from './attribution';

// URL slug (first segment after `/services/`) → DataCentral product group.
// Mirrors the scope→group mapping in `lead-product-group.ts`, keyed by the route
// slug instead of the provider scope. Non-product consoles (healthcheck,
// private-zones, organizations) are intentionally absent — they never tag a lead.
const SLUG_TO_GROUP: Record<string, string> = {
  compute: PRODUCT_GROUP.cloudService,
  kubernetes: PRODUCT_GROUP.cloudService,
  database: PRODUCT_GROUP.cloudService,
  oss: PRODUCT_GROUP.storage,
  cdn: PRODUCT_GROUP.cdn,
  waap: PRODUCT_GROUP.waap,
  waf: PRODUCT_GROUP.waf,
  certinvent: PRODUCT_GROUP.ssl,
  fastchannel: PRODUCT_GROUP.media,
  live: PRODUCT_GROUP.media,
  vod: PRODUCT_GROUP.media,
  aes: PRODUCT_GROUP.media,
};

const STORAGE_KEY = 'partnercfg.serviceInterest';

/** Product group for a `/services/<slug>` URL or path, or undefined when the path
 *  isn't a mapped service page. Accepts a full URL or a bare pathname. */
function groupFromPath(url: string | undefined): string | undefined {
  if (!url) return undefined;
  let path = url;
  try {
    path = new URL(url, 'https://x').pathname;
  } catch {
    // already a bare path — use as-is
  }
  const m = path.match(/\/services\/([^/?#]+)/);
  return m ? SLUG_TO_GROUP[m[1].toLowerCase()] : undefined;
}

/** Remember the service a visitor intended to open — call when an unauthenticated
 *  visitor is bounced from `/services/<slug>` to auth, so a later signup lead can
 *  still carry the interested product. No-op for non-service paths. */
export function rememberServiceInterest(pathname: string): void {
  const group = groupFromPath(pathname);
  if (!group) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, group);
  } catch {
    // best-effort — ignore storage failures (private mode / quota)
  }
}

/** The interested product group for this session's lead, inferred from the
 *  remembered service page (bounce) or the first-touch landing URL. Undefined
 *  when neither points at a mapped service. */
export function getServiceInterestGroup(): string | undefined {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // ignore — fall back to the landing URL
  }
  return groupFromPath(getAttribution()?.landing_url);
}
